import type {
  NotificationPage,
  NotificationSettings,
  NotificationSync,
  UnreadCount,
  WebPushConfiguration,
} from './types';

export const MESSAGE_API_URL = (process.env.NEXT_PUBLIC_MESSAGE_API ?? '').trim();

async function request<T>(path: string, accessToken: string, init?: RequestInit): Promise<T> {
  const response = await fetch(`${MESSAGE_API_URL}${path}`, {
    ...init,
    headers: {
      Accept: 'application/json',
      Authorization: `Bearer ${accessToken}`,
      ...(init?.body ? { 'Content-Type': 'application/json' } : {}),
      ...init?.headers,
    },
    cache: 'no-store',
  });

  if (!response.ok) {
    throw new Error(`message-api request failed: ${response.status}`);
  }

  return response.json() as Promise<T>;
}

export function getUnreadCount(accessToken: string): Promise<UnreadCount> {
  return request('/api/v1/communication/unread-count', accessToken);
}

export function getNotifications(
  accessToken: string,
  options: { cursor?: string | null; limit?: number; category?: string } = {},
): Promise<NotificationPage> {
  const search = new URLSearchParams();
  search.set('limit', String(options.limit ?? 20));
  if (options.cursor) search.set('cursor', options.cursor);
  if (options.category) search.set('category', options.category);

  return request(`/api/v1/notifications?${search}`, accessToken);
}

export function syncNotifications(
  accessToken: string,
  after: number,
  limit = 500,
): Promise<NotificationSync> {
  const search = new URLSearchParams({
    after: String(after),
    limit: String(limit),
  });
  return request(`/api/v1/notifications/sync?${search}`, accessToken);
}

export function markNotificationsSeen(
  accessToken: string,
  ids: string[],
): Promise<{ seenIds: string[]; unread: UnreadCount }> {
  return request('/api/v1/notifications/seen', accessToken, {
    method: 'POST',
    body: JSON.stringify({ ids }),
  });
}

export function getNotificationSettings(accessToken: string): Promise<NotificationSettings> {
  return request('/api/v1/notification-settings', accessToken);
}

export function updateNotificationSettings(
  accessToken: string,
  patch: Partial<NotificationSettings>,
): Promise<NotificationSettings> {
  return request('/api/v1/notification-settings', accessToken, {
    method: 'PATCH',
    body: JSON.stringify(patch),
  });
}

export function getWebPushConfiguration(accessToken: string): Promise<WebPushConfiguration> {
  return request('/api/v1/web-push/config', accessToken);
}

export function registerWebPushSubscription(
  accessToken: string,
  subscription: PushSubscriptionJSON,
): Promise<{ success: true }> {
  return request('/api/v1/web-push/subscriptions', accessToken, {
    method: 'POST',
    body: JSON.stringify(subscription),
  });
}

export function deleteWebPushSubscription(
  accessToken: string,
  endpoint: string,
): Promise<{ success: true }> {
  return request('/api/v1/web-push/subscriptions', accessToken, {
    method: 'DELETE',
    body: JSON.stringify({ endpoint }),
  });
}
